import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';
import SeniorExitIntentPopup from '../../components/feature/SeniorExitIntentPopup';
import SeniorPlanQuiz from '../../components/feature/SeniorPlanQuiz';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
const PAGE_URL = `${SITE_URL}/no-medical-exam`;

const faqs = [
  { q: 'What is no medical exam life insurance?', a: 'No medical exam life insurance lets you get covered without a doctor visit, blood draw, or urine sample. You answer a short health questionnaire online or by phone, and most applicants get approved within 24–48 hours — sometimes the same day. This type of coverage is ideal for busy Indiana professionals, those with minor health conditions, or anyone who wants fast approval without the hassle of scheduling and completing a medical examination.' },
  { q: 'How much coverage can I get without a medical exam in Indiana?', a: 'Most Indiana residents can qualify for up to $1,000,000 in no-exam term life coverage if they are under 60 and in good health. Simplified issue policies typically offer $50,000 to $500,000 in coverage. Final expense policies (no exam, guaranteed acceptance) typically offer $5,000–$50,000. The exact amount depends on your age, health profile, and the insurance carrier. Accelerated underwriting can approve higher amounts for qualified applicants.' },
  { q: 'Is no medical exam life insurance more expensive?', a: 'Slightly — typically 10–20% more than fully underwritten policies. But for many Indiana families, the speed, convenience, and certainty of approval outweigh the small cost difference. For example, a $500,000 policy might cost $35/month with no exam versus $28/month with a full medical exam. The premium difference is often worth it for busy professionals, those with health concerns, or anyone who needs coverage immediately.' },
  { q: 'Who qualifies for no medical exam life insurance in Indiana?', a: 'Most healthy adults under 60 qualify for simplified issue policies. Applicants with well-controlled conditions like high blood pressure, high cholesterol, or type 2 diabetes may also qualify. Guaranteed issue policies are available for ages 50–85 with no health questions at all — you cannot be denied. Accelerated underwriting is available for very healthy applicants who want the best rates without an exam. Your Indiana agent will help determine which option is best for your situation.' },
  { q: 'How fast can I get covered?', a: 'Many no-exam policies offer same-day or next-day coverage. Once approved, your policy is active immediately after your first payment. Some carriers offer instant approval in under 10 minutes using accelerated underwriting algorithms. Simplified issue policies typically take 24-48 hours for approval. Guaranteed issue policies provide immediate acceptance with no waiting period for accidental death coverage, though natural death coverage may have a 2-year waiting period.' },
  { q: 'Can I convert a no-exam policy to a permanent policy later?', a: 'Yes. Most term life policies — including no-exam versions — include a conversion option that lets you switch to whole life or universal life without a new medical exam, typically within the first 10–20 years. This is valuable if your health declines or your needs change. The conversion preserves your insurability and allows you to lock in permanent coverage without additional underwriting. Your Indiana agent can explain the conversion options available with your specific policy.' },
];

const types = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Simplified Issue Term Life',
    tag: 'Most Popular',
    tagColor: 'bg-[#D97642] text-white',
    desc: 'Answer 5–10 simple health questions. No exam. Get up to $1 million in protection. This is what most healthy Indiana adults under 60 choose — affordable, fast, and straightforward.',
    bullets: ['Coverage: $50K – $1M', 'Ages: 18–60', 'Approval: 24–48 hours', 'Terms: 10, 20, or 30 years'],
    color: 'border-[#D97642]',
  },
  {
    icon: 'ri-heart-pulse-fill',
    title: 'Simplified Issue Whole Life',
    tag: 'Permanent Coverage',
    tagColor: 'bg-[#1A3A52] text-white',
    desc: 'Coverage that never expires and actually grows in value — no exam required. It\'s like renting vs. owning. This policy is yours forever and builds real cash you can use.',
    bullets: ['Coverage: $25K – $500K', 'Ages: 18–65', 'Approval: 24–72 hours', 'Builds cash value'],
    color: 'border-[#1A3A52]',
  },
  {
    icon: 'ri-hand-heart-fill',
    title: 'Guaranteed Issue Life Insurance',
    tag: 'No Health Questions',
    tagColor: 'bg-[#2D6A4F] text-white',
    desc: 'You cannot be turned down. Zero health questions, zero exam, zero judgment. If you\'re 50–85 with serious health issues, this is your option.',
    bullets: ['Coverage: $5K – $50K', 'Ages: 50–85', 'Approval: Instant', 'No health questions ever'],
    color: 'border-[#2D6A4F]',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Accelerated Underwriting',
    tag: 'Best Rates, No Exam',
    tagColor: 'bg-[#7B4F2E] text-white',
    desc: 'The best of both worlds. Carriers use data instead of a physical exam. Very healthy applicants get the lowest possible rates — without stepping foot in a doctor\'s office.',
    bullets: ['Coverage: Up to $3M', 'Ages: 18–60', 'Approval: Same day', 'Full underwriting rates'],
    color: 'border-[#7B4F2E]',
  },
];

const steps = [
  { num: '01', title: 'Tell Us About Yourself', desc: 'Fill out our quick form or call (317) 648-2983. Just your age, how much coverage you want, and a few basic health details. Takes 2 minutes.' },
  { num: '02', title: 'Answer a Few Health Questions', desc: 'About 5–10 questions — online or by phone. No needles. No doctor. No waiting room. Most people finish in under 10 minutes.' },
  { num: '03', title: 'Get Your Approval', desc: 'Most Indiana applicants hear back within 24–48 hours. Healthy applicants using accelerated underwriting often get approved the same day.' },
  { num: '04', title: 'You\'re Covered — Starting Today', desc: 'Make your first payment and your family is protected immediately. Your policy documents arrive by email and mail. Done.' },
];

export default function NoMedicalExamPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'No Medical Exam Life Insurance Indiana | Fast Approval';

    const setOrCreate = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    setOrCreate('description', 'Get life insurance in Indiana without a medical exam. Fast approval, simplified underwriting & competitive rates. Perfect for busy professionals and health concerns.');
    setOrCreate('keywords', 'no medical exam life insurance Indiana, simplified issue life insurance, fast approval life insurance, no exam term life');
    setOrCreate('last-modified', new Date().toISOString());
    setOrCreate('og:title', 'No Medical Exam Life Insurance Indiana | Fast Approval | Hoosier Life Insurance');
    setOrCreate('og:description', 'Get life insurance without a medical exam in Indiana. Fast approval and competitive rates for busy professionals.');
    setOrCreate('og:url', 'https://hoosierlifeinsurance.com/no-medical-exam');
    setOrCreate('og:type', 'website');
    setOrCreate('og:site_name', 'Hoosier Life Insurance');
    setOrCreate('og:image', 'https://public.readdy.ai/ai/img_res/0029521139885ecfad4d48cc47ebcb79.jpg');
    setOrCreate('og:image:width', '1200');
    setOrCreate('og:image:height', '630');
    setOrCreate('twitter:card', 'summary_large_image');
    setOrCreate('twitter:title', 'No Medical Exam Life Insurance Indiana | Fast Approval');
    setOrCreate('twitter:description', 'Get life insurance without a medical exam. Fast approval and competitive rates for Indiana residents.');
    setOrCreate('twitter:image', 'https://readdy.ai/api/search-image?query=warm%20confident%20Indiana%20senior%20couple%20in%20their%2060s%20smiling%20while%20using%20a%20tablet%20computer%20at%20home%20applying%20for%20life%20insurance%20online%20bright%20living%20room%20natural%20light%20no%20stress%20easy%20process%20modern%20comfortable%20home&width=1200&height=630&seq=noexam-og-senior-v2&orientation=landscape');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', PAGE_URL);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${SITE_URL}/no-medical-exam#service`,
        name: 'No Medical Exam Life Insurance Indiana',
        description: 'Get life insurance without a medical exam in Indiana. Fast approval, simplified underwriting, and coverage up to $1,000,000 for qualified applicants.',
        provider: { '@id': `${SITE_URL}/#organization` },
        areaServed: { '@type': 'State', name: 'Indiana' },
        serviceType: 'No Medical Exam Life Insurance',
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '98', bestRating: '5', worstRating: '1' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/no-medical-exam#webpage`,
        url: PAGE_URL,
        name: 'No Medical Exam Life Insurance Indiana | Fast Approval Coverage',
        description: 'Get life insurance without a medical exam in Indiana. Simplified issue, guaranteed issue, and accelerated underwriting options. Coverage up to $1,000,000.',
        inLanguage: 'en-US',
        datePublished: '2024-01-15T00:00:00-05:00',
        dateModified: new Date().toISOString(),
        isPartOf: { '@id': `${SITE_URL}/#website` },
        breadcrumb: { '@id': `${SITE_URL}/no-medical-exam#breadcrumb` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${SITE_URL}/no-medical-exam#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'No Medical Exam Life Insurance', item: PAGE_URL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/no-medical-exam#faq`,
        mainEntity: [
          { '@type': 'Question', name: 'How much does no medical exam life insurance cost in Indiana?', acceptedAnswer: { '@type': 'Answer', text: 'Simplified issue policies typically cost 15-25% more than fully underwritten policies. A healthy 35-year-old might pay $35-45/month for $250,000 in coverage, compared to $25-30/month with a medical exam.' } },
          { '@type': 'Question', name: 'Can I get $500,000 in coverage without a medical exam?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, but it depends on your age and health profile. Accelerated underwriting can approve up to $500,000 for healthy applicants under 50. Simplified issue policies typically max out at $250,000-$300,000.' } },
          { '@type': 'Question', name: 'How fast can I get approved for no exam life insurance?', acceptedAnswer: { '@type': 'Answer', text: 'Accelerated underwriting can provide same-day approval for qualified applicants. Simplified issue policies typically take 2-5 business days. Guaranteed issue policies offer immediate acceptance.' } },
          { '@type': 'Question', name: 'What health conditions qualify for no medical exam life insurance?', acceptedAnswer: { '@type': 'Answer', text: 'Simplified issue policies accept controlled diabetes, high blood pressure, high cholesterol, and past cancer (5+ years in remission). Guaranteed issue policies accept all health conditions.' } },
          { '@type': 'Question', name: 'Is no medical exam life insurance worth it?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, if you need coverage quickly, have health concerns that would delay traditional underwriting, or want to avoid medical exams. The convenience and faster approval make it worthwhile for many Indiana families.' } },
          { '@type': 'Question', name: 'Can I convert no exam term life insurance to permanent coverage?', acceptedAnswer: { '@type': 'Answer', text: 'Most no medical exam term policies include conversion options, allowing you to convert to whole life or universal life insurance within 10-20 years without additional underwriting.' } },
        ],
      },
    ];

    schemas.forEach((schema, idx) => {
      const id = `schema-no-exam-${idx}`;
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
      el.textContent = JSON.stringify(schema);
    });

    // HowTo schema — critical for AI/voice search on step-by-step queries
    const howToSchema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      '@id': `${SITE_URL}/no-medical-exam#howto`,
      name: 'How to Get Life Insurance Without a Medical Exam in Indiana',
      description: 'Get life insurance in Indiana without a doctor visit, blood draw, or medical exam. Most Indiana applicants get approved same-day or within 24 hours.',
      totalTime: 'PT15M',
      estimatedCost: {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: '0',
        name: 'Free — no cost to apply',
      },
      supply: [
        { '@type': 'HowToSupply', name: 'Basic health information (dates of diagnoses, medications)' },
        { '@type': 'HowToSupply', name: 'Social Security number' },
        { '@type': 'HowToSupply', name: 'Beneficiary name and date of birth' },
      ],
      tool: [
        { '@type': 'HowToTool', name: 'Phone or computer' },
        { '@type': 'HowToTool', name: 'Licensed Indiana insurance agent (free)' },
      ],
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Tell Us About Yourself',
          text: 'Fill out a short form with your age, the coverage amount you want, and a few basic health details. Takes 2 minutes. Free, no obligation. Call (317) 648-2983 or submit online at hoosierlifeinsurance.com.',
          url: `${SITE_URL}/no-medical-exam#step-1`,
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Answer a Few Health Questions',
          text: 'Complete 5–10 health questions online or by phone. No needles. No doctor visit. No waiting room. No physical exam of any kind. Most applicants finish in under 10 minutes.',
          url: `${SITE_URL}/no-medical-exam#step-2`,
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Receive Your Approval',
          text: 'Most Indiana applicants receive a decision within 24–48 hours. Healthy applicants using accelerated underwriting are often approved the same day — sometimes in under 10 minutes.',
          url: `${SITE_URL}/no-medical-exam#step-3`,
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Coverage Starts Immediately',
          text: 'Make your first payment and your family is protected starting that same day. Policy documents arrive by email and mail. You can cancel within 30 days for a full refund.',
          url: `${SITE_URL}/no-medical-exam#step-4`,
        },
      ],
    };

    const howToEl = document.createElement('script');
    howToEl.id = 'schema-no-exam-howto';
    howToEl.type = 'application/ld+json';
    howToEl.textContent = JSON.stringify(howToSchema);
    document.head.appendChild(howToEl);

    return () => {
      [0, 1, 2, 3].forEach(idx => {
        const el = document.getElementById(`schema-no-exam-${idx}`);
        if (el) el.remove();
      });
      document.getElementById('schema-no-exam-howto')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </Link>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">No Medical Exam Life Insurance</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D97642]/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-xs font-semibold uppercase tracking-widest rounded-full mb-6">
                <i className="ri-stethoscope-line"></i>
                No Doctor Visit Required
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                No Medical Exam Life Insurance —<br />
                <span className="text-[#D97642]">Get Approved Fast in Indiana</span><br />
                <span className="text-3xl sm:text-4xl text-white/80">Indiana</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 mb-4 leading-relaxed">
                No doctor. No blood draw. No waiting room. Just answer a few questions and most Indiana residents are approved within 24 hours — sometimes the same afternoon.
              </p>
              <p className="text-base text-gray-400 mb-8 leading-relaxed">
                Seniors 50–85: you <strong className="text-white">cannot be denied.</strong> No health questions. No medical history. Guaranteed acceptance, full stop.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  to="/book-appointment"
                  className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
                >
                  <i className="ri-file-list-3-line mr-2 text-lg"></i>
                  Get Covered Today
                </Link>
                <a
                  href="tel:+13176482983"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2 text-lg"></i>
                  (317) 648-2983
                </a>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-time-line', label: 'Same-Day Approval' },
                  { icon: 'ri-stethoscope-line', label: 'No Exam Required' },
                  { icon: 'ri-shield-check-line', label: 'A+ Rated Carriers' },
                ].map((item, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4 text-center">
                    <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                      <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                    </div>
                    <div className="text-white text-xs font-semibold leading-tight">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <h3 className="text-[#1A3A52] font-bold text-xl mb-2">No-Exam Coverage at a Glance</h3>
                <p className="text-gray-500 text-sm mb-6">Sample rates for Indiana residents — healthy non-smokers</p>
                <div className="space-y-3">
                  {[
                    { profile: 'Age 30, $500K, 30-yr Term', price: '~$22/mo', badge: 'Best Value' },
                    { profile: 'Age 40, $500K, 20-yr Term', price: '~$38/mo', badge: 'Popular' },
                    { profile: 'Age 50, $250K, 10-yr Term', price: '~$72/mo', badge: 'Fast Approval' },
                    { profile: 'Age 65, $25K, Final Expense', price: '~$55/mo', badge: 'Guaranteed' },
                  ].map((row, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#F9F7F4] rounded-xl px-5 py-3">
                      <div>
                        <div className="text-[#1A3A52] font-semibold text-sm">{row.profile}</div>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-[#D97642]/10 text-[#D97642] text-xs font-semibold rounded-full">{row.badge}</span>
                      </div>
                      <div className="text-[#D97642] font-black text-lg">{row.price}</div>
                    </div>
                  ))}
                </div>
                <Link to="/book-appointment" className="mt-6 w-full inline-flex items-center justify-center px-6 py-3 bg-[#1A3A52] text-white font-bold rounded-lg hover:bg-[#0F2A3F] transition-all whitespace-nowrap">
                  Get Your Exact Rate — Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways — AI SEO Signal */}
      <section className="py-8 bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg mb-3">No Medical Exam Life Insurance — 6 Things to Know Right Now</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'You can get up to $1 million in coverage — with zero needles or doctor visits — using accelerated underwriting',
                  'Most Indiana applicants get a yes or no in 24–48 hours. Some get approved in under 10 minutes.',
                  'If you\'re 50–85 years old, guaranteed issue means you literally cannot be turned down. No exceptions.',
                  'No-exam costs about 10–20% more than full-exam coverage — often less than $5–$10 extra per month',
                  'High blood pressure? Type 2 diabetes? Controlled conditions usually still qualify for simplified issue',
                  'Most no-exam term policies let you convert to permanent coverage later — no new exam required',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Is This Right for You?</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">Who Is No-Exam Life Insurance Perfect For?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">If any of these sound like you, no-exam coverage is probably your fastest path to protection.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-run-line', title: 'Busy Professionals', desc: 'You don\'t have time to sit in a waiting room for a stranger to draw your blood. Get covered from your phone in the time it takes to eat lunch.' },
              { icon: 'ri-heart-line', title: 'Minor Health Conditions', desc: 'Well-controlled blood pressure, cholesterol, or type 2 diabetes? You likely still qualify. Don\'t assume a full exam is your only option.' },
              { icon: 'ri-parent-line', title: 'New Parents', desc: 'Just had a baby? Your family needs coverage NOW — not in 6 weeks after a lab processes your bloodwork. No-exam gets you covered fast.' },
              { icon: 'ri-elderly-line', title: 'Seniors (50–85)', desc: 'Guaranteed issue: zero health questions, zero exam, zero chance of being turned down. If you\'re breathing, you\'re covered. Period.' },
              { icon: 'ri-briefcase-line', title: 'Self-Employed', desc: 'No employer benefits? Get your own policy fast, without rearranging your work schedule for doctor appointments and lab visits.' },
              { icon: 'ri-refresh-line', title: 'Adding or Replacing Coverage', desc: 'Need more coverage without another full underwriting process? No-exam lets you layer on protection without starting from scratch.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-[#D97642]/30 hover:shadow-md transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                </div>
                <h3 className="font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of No-Exam Policies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-[#F7F9FB] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Policy Types</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">4 Types of No-Exam Life Insurance in Indiana</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Different options for different needs — we'll help you find the right fit.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-7">
            {types.map((t, i) => (
              <div key={i} className={`bg-white rounded-2xl border-2 ${t.color} shadow-sm hover:shadow-lg transition-all p-7`}>
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#F9F7F4] rounded-xl flex-shrink-0">
                    <i className={`${t.icon} text-[#1A3A52] text-2xl`}></i>
                  </div>
                  <div>
                    <span className={`inline-block px-3 py-0.5 ${t.tagColor} text-xs font-bold rounded-full mb-2`}>{t.tag}</span>
                    <h3 className="text-xl font-bold text-[#1A3A52]">{t.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-5">{t.desc}</p>
                <ul className="grid grid-cols-2 gap-2">
                  {t.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#D97642] flex-shrink-0"></i>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">4 Steps. No Waiting Room.</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Start to finish in under an hour. Here&apos;s exactly how simple it is.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 flex gap-5 items-start">
                <div className="text-4xl font-black text-[#D97642]/20 leading-none flex-shrink-0">{step.num}</div>
                <div>
                  <h3 className="font-bold text-[#1A3A52] text-lg mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/book-appointment" className="inline-flex items-center gap-2 px-10 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg text-lg whitespace-nowrap">
              <i className="ri-shield-check-line text-xl"></i>
              Start My Application — Free
            </Link>
            <p className="mt-3 text-sm text-gray-500">Or call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> to apply by phone</p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">No-Exam vs. Traditional Life Insurance</h2>
            <p className="text-lg text-gray-600">Is skipping the exam worth it? Here&apos;s the honest side-by-side comparison.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#1A3A52] text-white">
                  <th className="px-6 py-4 text-left font-semibold">Feature</th>
                  <th className="px-6 py-4 text-center font-semibold">No Medical Exam</th>
                  <th className="px-6 py-4 text-center font-semibold">Traditional (Full Exam)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Doctor Visit', '✗ Not required', '✓ Required'],
                  ['Blood / Urine Test', '✗ Not required', '✓ Required'],
                  ['Approval Time', '24 hrs – Same day', '2–6 weeks'],
                  ['Coverage Amount', 'Up to $1M+', 'Up to $10M+'],
                  ['Cost', 'Slightly higher', 'Lowest rates'],
                  ['Best For', 'Speed & convenience', 'Maximum coverage & savings'],
                  ['Health Conditions', 'Minor conditions OK', 'Full review required'],
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}>
                    <td className="px-6 py-4 font-semibold text-[#1A3A52]">{row[0]}</td>
                    <td className="px-6 py-4 text-center text-gray-700">{row[1]}</td>
                    <td className="px-6 py-4 text-center text-gray-500">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-center text-sm text-gray-500">
            Not sure which is right for you?{' '}
            <a href="tel:+13176482983" className="text-[#D97642] font-semibold hover:underline">(317) 648-2983</a>
            {' '}— our Indiana agents will help you decide in minutes.
          </p>
        </div>
      </section>

      {/* Visible FAQ Prose — crawlable content for search engines */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-[#F9F7F4] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Quick Answers</span>
            <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">No Medical Exam Life Insurance — What You Need to Know</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Straightforward answers to the most common questions from Indiana residents considering no-exam coverage.</p>
          </div>
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-2">What is no medical exam life insurance?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">No medical exam life insurance lets you get covered without a doctor visit, blood draw, or urine sample. You answer a short health questionnaire online or by phone, and most Indiana applicants get approved within 24–48 hours — sometimes the same day. This type of coverage is ideal for busy professionals, those with minor health conditions, or anyone who wants fast approval without the hassle of scheduling a medical examination.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-2">How much coverage can I get without a medical exam in Indiana?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Most Indiana residents can qualify for up to $1,000,000 in no-exam term life coverage if they are under 60 and in good health. Simplified issue policies typically offer $50,000 to $500,000 in coverage. Final expense policies — guaranteed acceptance with no exam — typically offer $5,000–$50,000. The exact amount depends on your age, health profile, and the insurance carrier. Accelerated underwriting can approve higher amounts for qualified applicants.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-2">Is no medical exam life insurance more expensive than traditional coverage?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Slightly — typically 10–20% more than fully underwritten policies. But for many Indiana families, the speed, convenience, and certainty of approval outweigh the small cost difference. For example, a $500,000 policy might cost $35/month with no exam versus $28/month with a full medical exam. The premium difference is often worth it for busy professionals, those with health concerns, or anyone who needs coverage immediately.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-2">Who qualifies for no medical exam life insurance in Indiana?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Most healthy adults under 60 qualify for simplified issue policies. Applicants with well-controlled conditions like high blood pressure, high cholesterol, or type 2 diabetes may also qualify. Guaranteed issue policies are available for ages 50–85 with no health questions at all — you cannot be denied. Accelerated underwriting is available for very healthy applicants who want the best rates without an exam. Your Indiana agent will help determine which option fits your situation.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-2">How fast can I get covered with no-exam life insurance?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Many no-exam policies offer same-day or next-day coverage. Once approved, your policy is active immediately after your first payment. Some carriers offer instant approval in under 10 minutes using accelerated underwriting algorithms. Simplified issue policies typically take 24–48 hours for approval. Guaranteed issue policies provide immediate acceptance with no waiting period for accidental death, though natural death coverage may have a 2-year waiting period.</p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-2">Can I convert a no-exam policy to permanent coverage later?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Yes. Most term life policies — including no-exam versions — include a conversion option that lets you switch to whole life or universal life without a new medical exam, typically within the first 10–20 years. This is valuable if your health declines or your needs change. The conversion preserves your insurability and allows you to lock in permanent coverage without additional underwriting. Your Indiana agent can explain the conversion options available with your specific policy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">No Medical Exam Life Insurance — FAQ</h2>
            <p className="text-lg text-gray-600">Answers to the most common questions from Indiana residents.</p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-[#F9F7F4] transition-colors cursor-pointer"
                >
                  <span className="font-semibold text-[#1A3A52] pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-[#D97642] text-xl flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-white">
                    <p className="text-gray-600 leading-relaxed text-sm">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Senior Plan Quiz */}
      <SeniorPlanQuiz />

      {/* ── Related Articles & Resources ── */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Continue Learning</span>
            <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">Related Articles &amp; Resources</h2>
            <p className="text-gray-600 max-w-xl mx-auto">Everything you need to understand no-exam life insurance and find the right coverage for your Indiana family.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {[
              {
                type: 'Blog',
                typeColor: 'bg-[#D97642] text-white',
                icon: 'ri-article-line',
                title: 'No Medical Exam Life Insurance in Indiana',
                desc: 'Complete guide to simplified issue, guaranteed acceptance, and accelerated underwriting options for Hoosiers.',
                to: '/blog/no-medical-exam-life-insurance-indiana',
              },
              {
                type: 'Blog',
                typeColor: 'bg-[#D97642] text-white',
                icon: 'ri-elderly-line',
                title: 'Life Insurance for Indiana Seniors',
                desc: 'Senior-focused coverage options from simplified issue term to guaranteed whole life — no exam required.',
                to: '/blog/life-insurance-indiana-seniors',
              },
              {
                type: 'Blog',
                typeColor: 'bg-[#D97642] text-white',
                icon: 'ri-heart-line',
                title: 'Burial Insurance in Indiana',
                desc: 'How final expense and burial insurance works — no medical exam, instant acceptance, peace of mind for your family.',
                to: '/blog/burial-insurance-indiana',
              },
              {
                type: 'Blog',
                typeColor: 'bg-[#D97642] text-white',
                icon: 'ri-price-tag-3-line',
                title: 'Cheapest Life Insurance in Indiana',
                desc: 'How to find the most affordable coverage — including no-exam options that won\'t break your budget.',
                to: '/blog/cheapest-life-insurance-indiana',
              },
              {
                type: 'Resource Guide',
                typeColor: 'bg-[#1A3A52] text-white',
                icon: 'ri-book-open-line',
                title: 'Final Expense Insurance for Indiana Seniors',
                desc: 'Deep-dive guide: guaranteed issue, simplified issue, costs, and how to choose the right plan at any age.',
                to: '/resources/final-expense-insurance-indiana-seniors',
              },
              {
                type: 'Resource Guide',
                typeColor: 'bg-[#1A3A52] text-white',
                icon: 'ri-calculator-line',
                title: 'How Much Coverage Do Indiana Families Need?',
                desc: 'Use the DIME method to calculate your exact coverage need — and see if no-exam policies can fill the gap.',
                to: '/resources/how-much-life-insurance-indiana-families-need',
              },
            ].map((card, i) => (
              <Link
                key={i}
                to={card.to}
                className="group bg-white rounded-xl border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all p-6 cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${card.typeColor}`}>{card.type}</span>
                </div>
                <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-lg mb-4 group-hover:bg-[#D97642] transition-colors">
                  <i className={`${card.icon} text-[#D97642] text-lg group-hover:text-white`}></i>
                </div>
                <h3 className="text-base font-bold text-[#1A3A52] mb-2 group-hover:text-[#D97642] transition-colors leading-snug">{card.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{card.desc}</p>
              </Link>
            ))}
          </div>

          {/* CTA Banner */}
          <div className="bg-[#1A3A52] rounded-2xl px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white font-bold text-lg mb-1">Ready to skip the exam and get covered today?</p>
              <p className="text-white/70 text-sm">Indiana-licensed agents available Mon–Fri 9AM–6PM · Sat 9AM–1PM · No pressure, no scripts</p>
            </div>
            <Link
              to="/book-appointment"
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap flex-shrink-0"
            >
              <i className="ri-calendar-check-line text-lg"></i>
              Book a Free Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* ── Related Services — Internal Links ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#F9F7F4] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">No-Exam Options by Product</span>
            <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">Which No-Exam Policy Is Right for You?</h2>
            <p className="text-gray-600 max-w-xl mx-auto">Every product below is available without a medical exam. Here&apos;s what makes each one different — in plain English.</p>
          </div>

          {/* Product-specific no-exam cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            <Link
              to="/final-expense"
              className="group bg-[#F9F7F4] rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-hand-heart-line text-2xl text-[#1A3A52] group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-[#1A3A52]">Final Expense Insurance</h3>
                    <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Guaranteed Issue</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    <strong>Zero health questions. Zero exam.</strong> Cannot be denied for any reason. Ages 50–85. The only truly 100% no-questions-asked no-exam option.
                  </p>
                  <ul className="space-y-1 mb-3">
                    {['Coverage: $5,000–$25,000', 'Approval: Instant', 'Ages 50–85 only', 'No health questions ever'].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#D97642]" />{f}
                      </li>
                    ))}
                  </ul>
                  <span className="flex items-center text-[#D97642] font-semibold text-sm">
                    See Final Expense Plans <i className="ri-arrow-right-line ml-1" />
                  </span>
                </div>
              </div>
            </Link>

            <Link
              to="/term-life"
              className="group bg-[#F9F7F4] rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-calendar-check-line text-2xl text-[#1A3A52] group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-[#1A3A52]">Term Life Insurance</h3>
                    <span className="bg-[#D97642] text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Most Popular</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    <strong>Simplified issue or accelerated underwriting.</strong> Up to $1M without an exam. Covers your mortgage, income, and family for 10–30 years.
                  </p>
                  <ul className="space-y-1 mb-3">
                    {['Coverage: up to $1M+', 'Approval: Same day–48 hrs', 'Ages 18–60', '5–10 health questions'].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#D97642]" />{f}
                      </li>
                    ))}
                  </ul>
                  <span className="flex items-center text-[#D97642] font-semibold text-sm">
                    See Term Life Plans <i className="ri-arrow-right-line ml-1" />
                  </span>
                </div>
              </div>
            </Link>

            <Link
              to="/whole-life"
              className="group bg-[#F9F7F4] rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-shield-check-line text-2xl text-[#1A3A52] group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-[#1A3A52]">Whole Life Insurance</h3>
                    <span className="bg-[#1A3A52] text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">Permanent</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    <strong>Simplified issue whole life — no exam, permanent coverage.</strong> Builds guaranteed cash value over time. Never expires as long as premiums are paid.
                  </p>
                  <ul className="space-y-1 mb-3">
                    {['Coverage: $25K–$500K', 'Approval: 24–72 hrs', 'Ages 18–65', 'Builds cash value'].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#D97642]" />{f}
                      </li>
                    ))}
                  </ul>
                  <span className="flex items-center text-[#D97642] font-semibold text-sm">
                    See Whole Life Plans <i className="ri-arrow-right-line ml-1" />
                  </span>
                </div>
              </div>
            </Link>

            <Link
              to="/mortgage-protection"
              className="group bg-[#F9F7F4] rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-home-heart-line text-2xl text-[#1A3A52] group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-[#1A3A52]">Mortgage Protection</h3>
                    <span className="bg-[#7B4F2E] text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">No Exam Available</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3 leading-relaxed">
                    <strong>Many mortgage protection plans require no medical exam.</strong> Just a few health questions. Protects your home if something happens to you.
                  </p>
                  <ul className="space-y-1 mb-3">
                    {['Coverage: matches mortgage balance', 'Approval: 24–72 hrs', 'No exam options available', 'Disability riders included'].map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-circle-fill text-[#D97642]" />{f}
                      </li>
                    ))}
                  </ul>
                  <span className="flex items-center text-[#D97642] font-semibold text-sm">
                    See Mortgage Protection Plans <i className="ri-arrow-right-line ml-1" />
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Quick comparison strip */}
          <div className="bg-[#1A3A52] rounded-2xl p-6 mb-8">
            <p className="text-center text-white font-bold text-sm mb-4 uppercase tracking-wider">At a Glance — No-Exam Option by Product</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { product: 'Final Expense', exam: 'None — Guaranteed', approval: 'Instant', coverage: '$5K–$25K', color: 'bg-green-500' },
                { product: 'Term Life', exam: 'Health Qs only', approval: 'Same day–48 hrs', coverage: 'Up to $1M+', color: 'bg-[#D97642]' },
                { product: 'Whole Life', exam: 'Health Qs only', approval: '24–72 hrs', coverage: '$25K–$500K', color: 'bg-white/20' },
                { product: 'Mortgage Prot.', exam: 'Health Qs only', approval: '24–72 hrs', coverage: 'Matches balance', color: 'bg-white/20' },
              ].map((item) => (
                <div key={item.product} className={`${item.color} rounded-xl p-4`}>
                  <p className="text-white font-bold text-sm mb-2">{item.product}</p>
                  <div className="space-y-1">
                    <p className="text-white/80 text-xs"><span className="text-white/60">Exam:</span> {item.exam}</p>
                    <p className="text-white/80 text-xs"><span className="text-white/60">Approval:</span> {item.approval}</p>
                    <p className="text-white/80 text-xs"><span className="text-white/60">Coverage:</span> {item.coverage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-600 mb-4 text-lg">Not sure which no-exam policy is right for you?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/instant-quote" className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-semibold rounded-lg hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                <i className="ri-calculator-line mr-2 text-xl" />
                Get a Free Instant Quote
              </Link>
              <a href="tel:+13176482983" className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-gray-300 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap">
                <i className="ri-phone-line mr-2 text-xl" />
                Call (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0F1F2E] via-[#1A3A52] to-[#0F1F2E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Skip the Doctor. Get Covered Today.</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Your family doesn&apos;t have to wait 6 weeks for a lab to process bloodwork. Most Indiana families get approved in 24 hours — or less. It starts with a free 15-minute call.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-appointment" className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap">
              <i className="ri-file-list-3-line mr-2 text-lg"></i>
              Get a Free Quote
            </Link>
            <a href="tel:+13176482983" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap">
              <i className="ri-phone-line mr-2 text-lg"></i>
              Call (317) 648-2983
            </a>
          </div>
          <p className="mt-6 text-white/40 text-xs">Licensed Indiana Agent · Indianapolis, IN 46256 · Mon–Fri 9AM–5PM · Sat 8AM–1PM</p>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={98} productName="No Medical Exam Life Insurance Indiana" />
      <ServingCommunitySection productName="No Medical Exam Life Insurance" productSlug="no-medical-exam" />
      <Footer />
      <MobileProductCTABar productName="No Medical Exam Life Insurance" quoteHref="/instant-quote" quoteLabel="Get Free Quote" />
      <SeniorExitIntentPopup />
    </div>
  );
}
